// [PURPOSE]: Async confirm dialog hook — replaces window.confirm() with styled dialog
// [WHY]: window.confirm() breaks dark theme; this provides promise-based async API
// [INTEGRATION]: Wrap in AppProviders, use via useConfirm() in components
// [USAGE]: const confirm = useConfirm();
//          if (await confirm({ title: '...', message: '...' })) { /* confirmed */ }

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: 'Confirm',
    message: 'Are you sure?',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmColor: 'blue',
  });

  // [PURPOSE]: Store resolve function for the pending promise
  const resolveRef = useRef(null);

  // [PURPOSE]: Open confirm dialog and return a promise that resolves true/false
  const confirm = useCallback(({
    title = 'Confirm',
    message = 'Are you sure?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmColor = 'blue',
  } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        confirmColor,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        confirmColor={dialogState.confirmColor}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * [PURPOSE]: Hook to access the async confirm function
 * [FALLBACK]: Returns window.confirm wrapper if used outside provider
 */
export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    // Fallback for components outside provider
    return ({ message = 'Are you sure?' } = {}) =>
      Promise.resolve(window.confirm(message));
  }
  return confirm;
};

export default ConfirmContext;
